import { useState, useEffect, useCallback, useRef } from 'react';

const LS_KEY = 'bmcl_pwa_dismissed';
const LS_INSTALLED = 'bmcl_pwa_installed';

let capturedPrompt = window.__bmcl_pwa_prompt || null;
let listeners = [];

function onBeforeInstall(e) {
  e.preventDefault();
  capturedPrompt = e;
  window.__bmcl_pwa_prompt = e;
  listeners.forEach(fn => fn(e));
}

function initCapture() {
  if (typeof window === 'undefined') return;
  if (window.__bmcl_pwa_captured) return;
  window.__bmcl_pwa_captured = true;
  window.addEventListener('beforeinstallprompt', onBeforeInstall);
}

initCapture();

export default function usePWAInstall({ cooldownDays = 0 } = {}) {
  const [deferredPrompt, setDeferredPrompt] = useState(capturedPrompt);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const engagementRef = useRef({ time: false, interactions: 0 });
  const timerRef = useRef(null);

  useEffect(() => {
    try {
      setIsInstalled(localStorage.getItem(LS_INSTALLED) === 'true');
    } catch {}
    setIsIOS(
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }, []);

  useEffect(() => {
    if (capturedPrompt && !deferredPrompt) {
      setDeferredPrompt(capturedPrompt);
    }
  }, [deferredPrompt]);

  useEffect(() => {
    const handler = (e) => {
      setDeferredPrompt(e);
      if (canShowNow()) setShowPrompt(true);
    };
    listeners.push(handler);
    if (capturedPrompt && canShowNow()) {
      setShowPrompt(true);
    }
    return () => {
      listeners = listeners.filter(fn => fn !== handler);
    };
  }, []);

  useEffect(() => {
    if (showPrompt) return;
    timerRef.current = setTimeout(() => {
      engagementRef.current.time = true;
      tryShow();
    }, 10000);

    const handleInteraction = () => {
      engagementRef.current.interactions += 1;
      tryShow();
    };
    window.addEventListener('click', handleInteraction, { once: true });
    window.addEventListener('touchstart', handleInteraction, { once: true });

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, [showPrompt]);

  function tryShow() {
    if (showPrompt) return;
    if (!capturedPrompt) return;
    if (!engagementRef.current.time || engagementRef.current.interactions < 2) return;
    if (canShowNow()) setShowPrompt(true);
  }

  const getDismissedTime = useCallback(() => {
    try {
      return parseInt(localStorage.getItem(LS_KEY), 10) || 0;
    } catch {
      return 0;
    }
  }, []);

  const canShowNow = useCallback(() => {
    if (isInstalled) return false;
    const t = getDismissedTime();
    return !t || Date.now() - t > cooldownDays * 86400000;
  }, [getDismissedTime, cooldownDays, isInstalled]);

  const dismiss = useCallback(() => {
    setShowPrompt(false);
    try { localStorage.setItem(LS_KEY, String(Date.now())); } catch {}
  }, []);

  const install = useCallback(async () => {
    if (isIOS) {
      setShowPrompt(false);
      return;
    }
    if (!capturedPrompt) return;
    setIsInstalling(true);
    capturedPrompt.prompt();
    const { outcome } = await capturedPrompt.userChoice;
    if (outcome === 'accepted') {
      try { localStorage.removeItem(LS_KEY); } catch {}
      try { localStorage.setItem(LS_INSTALLED, 'true'); } catch {}
      setIsInstalled(true);
    }
    capturedPrompt = null;
    setIsInstalling(false);
    setShowPrompt(false);
  }, [isIOS]);

  useEffect(() => {
    const onInstalled = () => {
      setShowPrompt(false);
      capturedPrompt = null;
      try { localStorage.setItem(LS_INSTALLED, 'true'); } catch {}
      setIsInstalled(true);
    };
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  return {
    showPrompt,
    isInstalling,
    isInstalled,
    isIOS,
    isInstallable: !!capturedPrompt || isIOS,
    install,
    dismiss,
  };
}
